import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/context/auth";
import useProjectRealtime from "@/hooks/useProjectRealtime";

interface Project {
  id: string;
  name: string;
  description?: string;
}

interface Folder {
  id: string;
  name: string;
  description: string;
  project_id: string;
  parent_folder_id: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  revised_by: string;
}

interface Assessment {
  id: string;
  name: string;
  description: string | null;
  folder_id: string;
  assessment_id: string;
  created_at: string;
  updated_at?: string;
  data?: any;
  status?: string;
  designer_completed_at?: string;
  designer_completed_by?: string;
  production_completed_at?: string;
  production_completed_by?: string;
  basu_ready_at?: string;
  type: 'risk_assessment' | 'skr_assessment' | 'work_preparation' | 'safety_round';
  project_id?: string;
  source_risk_assessment_id?: string;
}

export interface UseProjectRootContentProps {
  projectId: string;
}

export function useProjectRootContent({ projectId }: UseProjectRootContentProps) {
  const { user } = useAuth();
  const [project, setProject] = useState<Project | null>(null);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProjectRootContent = useCallback(async () => {
    if (!user || !projectId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch project info
      const { data: projectData, error: projectError } = await supabase
        .from("projects")
        .select("*")
        .eq("id", projectId)
        .maybeSingle();

      if (projectError) {
        throw new Error(`Could not fetch project: ${projectError.message}`);
      }

      setProject(projectData);

      // If no project is visible (no access via RLS), stop here
      if (!projectData) {
        setFolders([]);
        setAssessments([]);
        return;
      }

      // Fetch root-level folders (folders with no parent_folder_id)
      const { data: foldersData, error: foldersError } = await supabase
        .from("folders")
        .select("*")
        .eq("project_id", projectId)
        .is("parent_folder_id", null)
        .order("name");

      if (foldersError) {
        throw new Error(`Could not fetch folders: ${foldersError.message}`);
      }

      setFolders(foldersData || []);

      // Hämta alla dokument i projektet, oavsett mapp, så inget "försvinner"
      const [riskRes, skrRes, workRes, roundRes] = await Promise.all([
        supabase
          .from("folder_risk_assessments")
          .select("id, name, description, folder_id, assessment_id, created_at, updated_at, data, project_id")
          .eq("project_id", projectId)
          .is("deleted_at", null),
        supabase
          .from("skr_assessments")
          .select("id, name, description, folder_id, assessment_id, created_at, updated_at, data, project_id")
          .eq("project_id", projectId)
          .is("deleted_at", null),
        supabase
          .from("work_preparations")
          .select("id, name, folder_id, project_id, data, created_at, updated_at, source_risk_assessment_id, status")
          .eq("project_id", projectId)
          .is("deleted_at", null),
        supabase
          .from("safety_rounds")
          .select("id, name, round_date, status, folder_id, project_id, created_at, updated_at")
          .eq("project_id", projectId),
      ]);

      const combined: Assessment[] = [
        ...((riskRes.data || []) as any[]).map((a) => ({
          ...a,
          type: "risk_assessment" as const,
          description: a.description || "",
        })),
        ...((skrRes.data || []) as any[]).map((a) => ({
          ...a,
          type: "skr_assessment" as const,
          description: a.description || "",
        })),
        ...((workRes.data || []) as any[]).map((a) => ({
          ...a,
          assessment_id: a.id,
          type: "work_preparation" as const,
          description: "",
        })),
        ...((roundRes.data || []) as any[]).map((r) => ({
          ...r,
          assessment_id: r.id,
          data: null,
          type: "safety_round" as const,
          description: r.round_date ? `Skyddsrond ${r.round_date}` : "Skyddsrond",
        })),
      ].sort((a, b) =>
        String(b.updated_at || b.created_at).localeCompare(String(a.updated_at || a.created_at))
      );

      setAssessments(combined);


    } catch (err: any) {
      console.error("Error fetching project root content:", err);
      setError(err.message);
      toast.error("Kunde inte hämta projektinnehåll", {
        description: err.message
      });
    } finally {
      setIsLoading(false);
    }
  }, [projectId, user]);

  useEffect(() => {
    fetchProjectRootContent();
  }, [fetchProjectRootContent]);

  // Håll listan aktuell automatiskt när något ändras i projektet.
  const { lastUpdatedAt } = useProjectRealtime(projectId, fetchProjectRootContent);

  return {
    project,
    folders,
    assessments,
    isLoading,
    error,
    lastUpdatedAt,
    refetch: fetchProjectRootContent,
  };
}